import React, { Component } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import GoogleMap from 'react-google-map';
import url from 'url';

import typeCheck from 'common/util/typeCheck';
import {
  getApartmentUrlPath,
  getApartmentPageMetadata,
} from 'common/apartmentHelpers';

import { SR_API_ACTION_GET_APARTMENT_BY_ID } from 'api/actionTypes';
import { SCREEN_ID_APARTMENT_MAP } from 'client/constants/screenIds';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withLastLocation from 'client/helpers/withLastLocation';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withGoogleMaps from 'client/helpers/withGoogleMaps';
import withApiState from 'client/helpers/withApiState';

import Navbar from 'client/components/Navbar';
import SrNavbarToastButton from 'client/components/SrNavbarToastButton';
import ActivityIndicator from 'client/components/ActivityIndicator';
import SrLink from 'client/components/SrLink/SrLink';

import { defaultMapOptions, defaultPolygonOptions } from 'client/constants/mapOptions';
import ApartmentActions from 'client/actions/Apartment';
import branch from 'client/core/branch';

@withTransitionHook
@withScreenId(SCREEN_ID_APARTMENT_MAP)
@withScrollToTop
@withGoogleMaps
@withLastLocation
@withApiState({
  apartmentApiState: {
    action: SR_API_ACTION_GET_APARTMENT_BY_ID,
  },
})
@branch((props) => ({
  apartment: ['apartments', 'byId', props.match.params.apartmentId],
  currentUserArea: ['currentUser', 'searchFilters', 'area'],
  appUrl: ['env', 'APP_URL'],
}))
class ApartmentMapPage extends Component {
  static propTypes = {
    apartment: PropTypes.object.isRequired,
    apartmentApiState: PropTypes.object.isRequired,
    currentUserArea: PropTypes.array,
    googleMaps: PropTypes.object,
    match: PropTypes.object.isRequired,
    appUrl: PropTypes.string.isRequired,
  }

  static fetchData(match, inject) {
    return ApartmentActions.getById(inject, {
      apartmentId: parseInt(match.params.apartmentId, 10),
    });
  }

  static getPageMetadata = (state, match) => {
    const apartment = state.get(['apartments', 'byId', match.params.apartmentId]);
    const appUrl = state.get(['env', 'APP_URL']);

    if (!apartment) {
      return {};
    }

    return getApartmentPageMetadata({ apartment, appUrl });
  }

  constructor(props) {
    super(props);

    const locationQuery = queryString.parse(props.location.search);

    this.state = {
      showStreetView: !!locationQuery.streetView,
    };
  }

  _onLoadedMap = (googleMaps, map) => {
    const {
      apartment,
      currentUserArea,
    } = this.props;
    const { showStreetView } = this.state;
    typeCheck('apartment::Object', apartment);

    const apartmentPosition = {
      lat: apartment.fieldsForDisplay.locationLatitude,
      lng: apartment.fieldsForDisplay.locationLongitude,
    };

    this.panorama = map.getStreetView();
    this.panorama.setPosition(apartmentPosition);
    this.panorama.setOptions({
      enableCloseButton: true,
    });
    if (showStreetView) {
      this.panorama.setVisible(true);
    }

    const marker = new googleMaps.Marker({
      position: apartmentPosition,
      map: map,
    });
    const infowindow = new googleMaps.InfoWindow({
      content: '<p>' + apartment.name + '</p>',
    });

    googleMaps.event.addListener(marker, 'click', () => {
      infowindow.open(map, marker);
    });

    googleMaps.event.addListener(this.panorama, 'visible_changed', () => {
      this.setState({ showStreetView: this.panorama.getVisible() });
    });

    if (currentUserArea) {
      const polygon = new googleMaps.Polygon({
        paths: currentUserArea.map((coord) => ({
          lng: coord[0],
          lat: coord[1],
        })),
        ...defaultPolygonOptions,
      });

      polygon.setMap(map);
    }
  }

  _onClickStreetViewOption = () => {
    const showStreetView = !this.state.showStreetView;
    this.panorama.setVisible(showStreetView);
  }

  render() {
    const {
      googleMaps,
      apartment,
      apartmentApiState,
      match,
      appUrl,
    } = this.props;

    const sunroomApartmentLink = apartment && url.resolve(appUrl, getApartmentUrlPath(apartment));

    return (
      <div className='page-listing'>
        <div className='listing-map'>
          <Navbar
            leftButton={
              <SrLink
                className='back link'
                restoreScrollPosition
                redirectToLastLocation
                to={`/apartment/${match.params.apartmentId}`}
              >
                Back
              </SrLink>
            }
            rightButton={(
              <SrNavbarToastButton
                toastLink={sunroomApartmentLink}
                toastContent='link copied!'
                buttonContent='Share'
                toggleToast
              />
            )}
          />
          <div>
            {!apartment || apartmentApiState.inProgress
              ? <ActivityIndicator />
              : (
                <div className='mapContainer'>
                  <div className='innerMapContainer'>
                    <GoogleMap
                      googleMaps={googleMaps}
                      center={{
                        lat: apartment.fieldsForDisplay.locationLatitude,
                        lng: apartment.fieldsForDisplay.locationLongitude,
                      }}
                      zoom={14}
                      disableDefaultUI
                      gestureHandling='greedy'
                      clickableIcons={false}
                      onLoaded={this._onLoadedMap}
                      options={defaultMapOptions}
                    />
                  </div>

                  <div className='streetview-button' onClick={this._onClickStreetViewOption}>
                    <span>street view</span>
                  </div>
                </div>
              )
            }
          </div>
        </div>
      </div>
    );
  }
}

ApartmentMapPage.displayName = 'ApartmentMapPage';

export default ApartmentMapPage;
